import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { FormCascadeRule } from '../../../../config/formCascadeConfig';
import type { ApiPPG, PPGRow } from '../../../../types/promoMetrics';
import { cascadeKPIs } from '../../utils/cascadeKPIs';
import { applyFormCascadeToRows, computeSeedForRow } from '../../utils/applyFormCascade';
import { initializeFromApi } from '../../utils/promoStateUtils';

interface UsePpgDataArgs {
    existingPpgData: ApiPPG[];
    existingPpgIds: number[];
    fetchedPpgData: ApiPPG[];
    formCascadeRules: FormCascadeRule[];
    promoFormValues: Record<string, unknown>;
}

interface UsePpgDataReturn {
    ppgData: PPGRow[];
    hasProducts: boolean;
    baseline: Map<string, Record<string, number>>;
    activeExistingPpgIds: number[] | null;
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    applyFormCascade: (formValues: Record<string, unknown>) => void;
    resetCascadeSeededMetrics: () => void;
    onAddProducts: (ppgIds: number[], existingPpgIds: number[]) => { newIds: number[] };
    resetEditableSpendsToZero: () => void;
    resetPpgToExisting: (existingRows: ApiPPG[]) => void;
    clearPpgState: () => void;
    snapshotBaseline: () => void;
}

const usePpgData = ({
    existingPpgData,
    existingPpgIds,
    fetchedPpgData,
    formCascadeRules,
    promoFormValues,
}: UsePpgDataArgs): UsePpgDataReturn => {
    const tpoGenConfig = useTpoGenConfig();
    // `formulaOnlyMetricsConfig` holds `fe_hidden` intermediates (e.g. gross_sales)
    // that are never displayed but must feed the cascade DAG so dependent grid
    // metrics (e.g. net_sales) resolve. Never surface these in the grid/pickers.
    const allConfigs = useMemo(
        () => [
            ...tpoGenConfig.promoMetricsConfig,
            ...tpoGenConfig.promoSpendsConfig,
            ...tpoGenConfig.formulaOnlyMetricsConfig,
        ],
        [tpoGenConfig],
    );
    const editableSpendKeys = useMemo(
        () => tpoGenConfig.promoSpendsConfig.filter((c) => c.editable).map((c) => c.key),
        [tpoGenConfig],
    );

    const [ppgData, setPpgData] = useState<PPGRow[]>([]);
    const [hasProducts, setHasProducts] = useState(false);
    const [activeExistingPpgIds, setActiveExistingPpgIds] = useState<number[] | null>(null);

    const baselineRef = useRef<Map<string, Record<string, number>>>(new Map());

    // Latest rules + form values for the init effects, read at init time without
    // re-running the effects on every keystroke.
    const formCascadeRef = useRef({ rules: formCascadeRules, values: promoFormValues });
    formCascadeRef.current = { rules: formCascadeRules, values: promoFormValues };

    const handleMetricChange = useCallback((ppgId: string, metricKey: string, newValue: number) => {
        setPpgData((prev) =>
            prev.map((ppg) => {
                if (String(ppg.ppg_id) !== ppgId) return ppg;

                const { values, cascadedKeys: newlyCascaded } = cascadeKPIs(
                    allConfigs,
                    metricKey,
                    newValue,
                    ppg.values,
                );
                const newUserEdited = new Set(ppg.userEditedKeys);
                newUserEdited.add(metricKey);

                // Cumulative: keep prior cascade marks so an earlier cascade isn't lost when a
                // later edit to a different metric cascades a different set. This lets the table
                // highlight rely on `userEditedKeys` / `cascadedKeys` alone (2 states, no baseline diff).
                const newCascaded = new Set(ppg.cascadedKeys);
                newlyCascaded.forEach((k) => newCascaded.add(k));

                return {
                    ...ppg,
                    values,
                    userEditedKeys: newUserEdited,
                    cascadedKeys: newCascaded,
                };
            }),
        );
    }, [allConfigs]);

    /**
     * Change-triggered form -> metric cascade: seeds every current row (saved +
     * recommended) via the same `onMetricChange` path a manual edit uses, so the
     * seed is marked user-edited (dirty + saved) and cascades to dependents.
     *
     * Multiple rules may target the same metric (e.g. dollar_off and percent_off
     * both target promotion_price). We resolve all rules per metric key first:
     * if ANY rule produces a non-null seed, that seed wins. Only when NO rule
     * produces a seed for a given metric do we reset it to baseline (handling the
     * case where a gating field change invalidates the previous seed).
     */
    const applyFormCascade = useCallback(
        (formValues: Record<string, unknown>) => {
            if (formCascadeRules.length === 0) return;
            for (const ppg of ppgData) {
                const ppgId = String(ppg.ppg_id);
                const baselineValues = baselineRef.current.get(ppgId);

                const seedByMetric = new Map<string, number | null>();
                for (const rule of formCascadeRules) {
                    const metricKey = rule.effect.metricKey;
                    const seed = computeSeedForRow(rule, ppg.values, formValues);
                    if (seed !== null) {
                        seedByMetric.set(metricKey, seed);
                    } else if (!seedByMetric.has(metricKey)) {
                        seedByMetric.set(metricKey, null);
                    }
                }

                for (const [metricKey, seed] of seedByMetric) {
                    if (seed !== null) {
                        handleMetricChange(ppgId, metricKey, seed);
                    } else {
                        const baselineVal = baselineValues?.[metricKey] ?? 0;
                        if (ppg.values[metricKey] !== baselineVal) {
                            handleMetricChange(ppgId, metricKey, baselineVal);
                        }
                    }
                }
            }
        },
        [formCascadeRules, ppgData, handleMetricChange],
    );

    /**
     * Reverts every form-cascade-seeded metric back to its baseline value (and
     * cascades that reset through the metric DAG). Called when a gating/unit field
     * changes that invalidates the previous seed (e.g. offer type toggle resets
     * the user input, so the old seeded metric no longer applies).
     */
    const resetCascadeSeededMetrics = useCallback(() => {
        if (formCascadeRules.length === 0 || ppgData.length === 0) return;
        const metricKeys = new Set(formCascadeRules.map((r) => r.effect.metricKey));
        for (const ppg of ppgData) {
            const ppgId = String(ppg.ppg_id);
            const baselineValues = baselineRef.current.get(ppgId);
            for (const key of metricKeys) {
                const baselineVal = baselineValues?.[key] ?? 0;
                if (ppg.values[key] !== baselineVal) {
                    handleMetricChange(ppgId, key, baselineVal);
                }
            }
        }
    }, [formCascadeRules, ppgData, handleMetricChange]);

    const resetEditableSpendsToZero = useCallback(() => {
        setPpgData((prev) =>
            prev.map((ppg) => {
                const resetValues = { ...ppg.values };
                const allResetKeys = new Set<string>();

                for (const key of editableSpendKeys) {
                    resetValues[key] = 0;
                    allResetKeys.add(key);
                }

                let finalValues = resetValues;
                const allCascaded = new Set<string>();
                for (const key of editableSpendKeys) {
                    const { values, cascadedKeys } = cascadeKPIs(
                        allConfigs,
                        key,
                        0,
                        finalValues,
                    );
                    finalValues = values;
                    cascadedKeys.forEach((k) => allCascaded.add(k));
                }

                return {
                    ...ppg,
                    values: finalValues,
                    userEditedKeys: new Set<string>(),
                    cascadedKeys: new Set([...allResetKeys, ...allCascaded]),
                };
            }),
        );
    }, [allConfigs, editableSpendKeys]);

    const handleAddProducts = useCallback(
        (ppgIds: number[], currentExistingPpgIds: number[]) => {
            if (ppgIds.length === 0) return { newIds: [] as number[] };
            const existingIdSet = new Set(currentExistingPpgIds);
            const newIds = ppgIds.filter((id) => !existingIdSet.has(id));
            const keptExistingIds = ppgIds.filter((id) => existingIdSet.has(id));

            setActiveExistingPpgIds(keptExistingIds);
            return { newIds };
        },
        [],
    );

    const snapshotBaseline = useCallback(() => {
        const snapshot = new Map<string, Record<string, number>>();
        for (const ppg of ppgData) {
            snapshot.set(String(ppg.ppg_id), { ...ppg.values });
        }
        baselineRef.current = snapshot;

        setActiveExistingPpgIds(null);

        setPpgData((prev) =>
            prev.map((ppg) => ({
                ...ppg,
                userEditedKeys: new Set<string>(),
                cascadedKeys: new Set<string>(),
            })),
        );
    }, [ppgData]);

    const resetPpgToExisting = useCallback((existingRows: ApiPPG[]) => {
        setActiveExistingPpgIds(null);

        if (existingRows.length > 0) {
            const { rows, snapshot } = initializeFromApi(existingRows);
            baselineRef.current = snapshot;
            setPpgData(rows);
        }
    }, []);

    const clearPpgState = useCallback(() => {
        setPpgData([]);
        setHasProducts(false);
        baselineRef.current = new Map();
        setActiveExistingPpgIds(null);
    }, []);

    useEffect(() => {
        if (existingPpgData.length > 0) {
            const { rows, snapshot } = initializeFromApi(existingPpgData);
            baselineRef.current = snapshot;
            setPpgData(rows);
            setHasProducts(true);
        }
    }, [existingPpgData]);

    useEffect(() => {
        const { rules, values } = formCascadeRef.current;

        if (activeExistingPpgIds === null) {
            if (fetchedPpgData.length > 0) {
                // Pure recommended rows: BE never saw the offer, so seed all of them.
                // Baseline must be the pre-cascade snapshot (raw recommended values) so a
                // cascade-seeded metric (e.g. promotion_price from $ off) reads as "changed
                // from baseline"; markUserEdited keeps it dirty so it lands in the save payload.
                const { rows, snapshot } = initializeFromApi(fetchedPpgData);
                const seededRows = applyFormCascadeToRows(rows, rules, values, allConfigs, {
                    markUserEdited: true,
                });
                baselineRef.current = snapshot;
                setPpgData(seededRows);
                setHasProducts(true);
            }
            return;
        }

        const existingIdSet = new Set(activeExistingPpgIds);
        const keptExistingRows = existingPpgData.filter((ppg) => existingIdSet.has(ppg.ppg_id));
        const allApiPpgs = [...keptExistingRows, ...fetchedPpgData];

        if (allApiPpgs.length > 0) {
            const { rows, snapshot } = initializeFromApi(allApiPpgs);
            // Seed only the newly-fetched rows; kept saved rows keep their BE values.
            const fetchedIdSet = new Set(fetchedPpgData.map((p) => String(p.ppg_id)));
            const seededRows = applyFormCascadeToRows(rows, rules, values, allConfigs, {
                onlyPpgIds: fetchedIdSet,
                markUserEdited: true,
            });
            // Baseline is the pre-cascade snapshot for every row: kept rows keep their true
            // saved BE values, newly-fetched rows keep their raw recommended values (not the
            // cascade-seeded ones), so the seeded metric diffs correctly against baseline.
            baselineRef.current = snapshot;
            setPpgData(seededRows);
            setHasProducts(true);
        } else {
            setPpgData([]);
            setHasProducts(false);
            baselineRef.current = new Map();
        }
    }, [activeExistingPpgIds, fetchedPpgData, existingPpgData, allConfigs]);

    return {
        ppgData,
        hasProducts,
        baseline: baselineRef.current,
        activeExistingPpgIds,
        onMetricChange: handleMetricChange,
        applyFormCascade,
        resetCascadeSeededMetrics,
        onAddProducts: handleAddProducts,
        resetEditableSpendsToZero,
        resetPpgToExisting,
        clearPpgState,
        snapshotBaseline,
    };
};

export default usePpgData;
